import React, { useState, useCallback, useMemo } from 'react';
import Box from '@mui/material/Box';
import { alpha, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import SearchIcon from '@mui/icons-material/Search';
import DensitySmallIcon from '@mui/icons-material/DensitySmall';
import DensityMediumIcon from '@mui/icons-material/DensityMedium';
import DensityLargeIcon from '@mui/icons-material/DensityLarge';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { DataGrid } from '@mui/x-data-grid';
import EmptyState from '../components/EmptyState';
import {
  RADIUS_MD,
  RADIUS_LG,
  SHADOW,
  BP_LAPTOP,
} from '../theme/tokens';

/**
 * DataTable — Enterprise Design System
 *
 * Wraps MUI X Data Grid (v6.10) with consistent enterprise styling,
 * sticky headers, column resize/reorder, pagination, bulk selection,
 * quick filter with debounce, empty state support, density selector,
 * and responsive behavior.
 *
 * Props:
 *  - columns: DataGrid column definitions (required)
 *  - rows: array of row data (required)
 *  - loading: boolean
 *  - pagination: boolean (default true)
 *  - pageSize: number (default 25)
 *  - checkboxSelection: boolean
 *  - quickFilter: boolean (default false) — show quick filter input
 *  - stickyHeader: boolean (default true)
 *  - densitySelector: boolean (default false) — show density toggle
 *  - emptyState: { icon, title, description } — shown when rows is empty
 *  - tableId: string — when set, the user's column widths and order are
 *      persisted to localStorage and restored on next visit
 *  - sx: additional sx overrides
 *  - ...rest: all other DataGrid props passed through
 *
 * Column customization:
 *  - Drag a column's right edge (separator) to resize it.
 *  - Drag a column header to reorder columns.
 *  - Set `resizable: false` on a column def to lock its width.
 */

// Debounce utility
function useDebounce(callback, delay) {
  const timerRef = React.useRef(null);

  const debouncedFn = useCallback(
    (value) => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      timerRef.current = setTimeout(() => {
        callback(value);
      }, delay);
    },
    [callback, delay]
  );

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  return debouncedFn;
}

/**
 * QuickFilterInput — internal toolbar component for quick filtering.
 * Requires 2+ characters before filtering, 300ms debounce.
 */
function QuickFilterInput({ onFilterChange }) {
  const [inputValue, setInputValue] = useState('');

  const applyFilter = useCallback(
    (value) => {
      // Only filter with 2+ characters, or clear when empty
      if (value.length >= 2 || value.length === 0) {
        onFilterChange(value);
      }
    },
    [onFilterChange]
  );

  const debouncedApply = useDebounce(applyFilter, 300);

  const handleChange = (e) => {
    const val = e.target.value;
    setInputValue(val);
    debouncedApply(val);
  };

  return (
    <TextField
      size="small"
      placeholder="Quick filter…"
      value={inputValue}
      onChange={handleChange}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
          </InputAdornment>
        ),
      }}
      sx={{
        minWidth: { xs: 0, sm: 240 },
        maxWidth: { xs: '100%', sm: 340 },
        flex: 1,
        '& .MuiOutlinedInput-root': {
          borderRadius: '8px',
          fontSize: '0.8125rem',
          bgcolor: (theme) => theme.palette.mode === 'dark' ? alpha('#ffffff', 0.03) : theme.palette.grey[50],
          transition: 'background-color 150ms ease, box-shadow 150ms ease',
          '& fieldset': {
            borderColor: 'divider',
          },
          '&:hover': {
            bgcolor: (theme) => theme.palette.mode === 'dark' ? alpha('#ffffff', 0.05) : theme.palette.grey[100],
          },
          '&.Mui-focused': {
            bgcolor: (theme) => theme.palette.mode === 'dark' ? alpha('#ffffff', 0.05) : theme.palette.background.paper,
            boxShadow: (theme) => `0 0 0 3px ${alpha(theme.palette.primary.main, 0.15)}`,
          },
        },
      }}
      aria-label="Quick filter table rows"
    />
  );
}

/**
 * NoRowsOverlay — shown when there are no rows (empty state).
 */
function NoRowsOverlay({ emptyState }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        minHeight: 200,
      }}
    >
      <EmptyState
        icon={emptyState?.icon}
        title={emptyState?.title}
        description={emptyState?.description}
      />
    </Box>
  );
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

// ── Resizable column header ───────────────────────────────
// MUI X Community does not support drag-to-resize, so we render our own header
// with a draggable handle on the right edge. Dragging updates the column width
// via a callback into DataTable, which stores it in state and persists it.
const RESIZE_MIN_WIDTH = 60;
const RESIZE_MAX_WIDTH = 900;

function ResizableColumnHeader({ colDef, onResize, onResizeCommit }) {
  const dragState = React.useRef(null);

  const handlePointerDown = useCallback(
    (e) => {
      // Ignore anything but the primary button; don't start a sort/reorder.
      if (e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();

      const startX = e.clientX;
      const startWidth = colDef.computedWidth || colDef.width || 100;
      dragState.current = { startX, startWidth, field: colDef.field };

      const handleMove = (moveEvent) => {
        if (!dragState.current) return;
        const delta = moveEvent.clientX - dragState.current.startX;
        const next = Math.min(
          RESIZE_MAX_WIDTH,
          Math.max(RESIZE_MIN_WIDTH, dragState.current.startWidth + delta)
        );
        onResize(dragState.current.field, next);
      };

      const handleUp = (upEvent) => {
        if (dragState.current) {
          const delta = upEvent.clientX - dragState.current.startX;
          const next = Math.min(
            RESIZE_MAX_WIDTH,
            Math.max(RESIZE_MIN_WIDTH, dragState.current.startWidth + delta)
          );
          onResizeCommit(dragState.current.field, next);
        }
        dragState.current = null;
        document.removeEventListener('pointermove', handleMove);
        document.removeEventListener('pointerup', handleUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };

      document.addEventListener('pointermove', handleMove);
      document.addEventListener('pointerup', handleUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    },
    [colDef, onResize, onResizeCommit]
  );

  // Double-click the handle resets this column to its default width.
  const handleDoubleClick = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      onResizeCommit(colDef.field, null);
    },
    [colDef.field, onResizeCommit]
  );

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        height: '100%',
        position: 'relative',
        pr: '10px',
      }}
    >
      <Box
        component="span"
        sx={{
          flex: 1,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {colDef.headerName ?? colDef.field}
      </Box>
      {colDef.resizable !== false && (
        <Box
          onPointerDown={handlePointerDown}
          onMouseDown={(e) => { e.stopPropagation(); }}
          onDoubleClick={handleDoubleClick}
          onClick={(e) => e.stopPropagation()}
          role="separator"
          aria-orientation="vertical"
          aria-label={`Resize ${colDef.headerName ?? colDef.field} column`}
          sx={{
            position: 'absolute',
            top: 0,
            right: -12,
            width: 24,
            height: '100%',
            cursor: 'col-resize',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 20,
            pointerEvents: 'auto',
            touchAction: 'none',
            '&:hover .resize-grip': {
              bgcolor: (theme) => theme.palette.primary.main,
              width: '3px',
            },
          }}
        >
          <Box
            className="resize-grip"
            sx={{
              width: '2px',
              height: '55%',
              borderRadius: '2px',
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.35),
              transition: 'background-color 120ms ease, width 120ms ease',
            }}
          />
        </Box>
      )}
    </Box>
  );
}

// ── Column layout persistence ─────────────────────────────
// When a `tableId` is supplied, the user's column widths and column order are
// stored in localStorage so their customizations survive reloads.
const layoutStorageKey = (tableId) => `datatable:layout:${tableId}`;

function loadLayout(tableId) {
  if (!tableId) return null;
  try {
    const raw = localStorage.getItem(layoutStorageKey(tableId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveLayout(tableId, layout) {
  if (!tableId) return;
  try {
    localStorage.setItem(layoutStorageKey(tableId), JSON.stringify(layout));
  } catch {
    /* ignore quota / serialization errors */
  }
}

// ── Mobile card view ──────────────────────────────────────────────────────────
// On phones a wide data grid is unusable even with horizontal scroll, so we
// render each row as a stacked card (label: value). Columns opt out of the card
// with `hideOnMobile: true`; the column flagged `primaryOnMobile: true` (or the
// first column) becomes the card title. Cell content reuses the column's
// renderCell / valueGetter so inline controls and badges still work.
function renderColumnValue(col, row) {
  const params = {
    id: row.id ?? row._id,
    row,
    field: col.field,
    value: col.valueGetter
      ? col.valueGetter({ row, field: col.field, value: row[col.field] })
      : row[col.field],
    colDef: col,
  };
  if (typeof col.renderCell === 'function') return col.renderCell(params);
  return params.value == null || params.value === '' ? '—' : String(params.value);
}

function MobileCardList({ columns, rows, onRowClick, getRowId, emptyState }) {
  if (!rows || rows.length === 0) {
    return <NoRowsOverlay emptyState={emptyState} />;
  }

  // Columns shown on cards: skip checkbox/actions-only and explicitly hidden.
  const cardCols = columns.filter(
    (c) => c.field !== '__check__' && c.field !== '__reorder__' && !c.hideOnMobile
  );
  const titleCol =
    cardCols.find((c) => c.primaryOnMobile) || cardCols[0];
  const detailCols = cardCols.filter((c) => c !== titleCol);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, py: 1 }}>
      {rows.map((row) => {
        const rid = getRowId ? getRowId(row) : (row.id ?? row._id);
        return (
          <Box
            key={rid}
            onClick={onRowClick ? () => onRowClick({ id: rid, row }) : undefined}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              bgcolor: 'background.paper',
              p: 1.5,
              cursor: onRowClick ? 'pointer' : 'default',
              transition: 'background-color 120ms ease',
              '&:active': onRowClick ? { bgcolor: 'action.hover' } : undefined,
            }}
          >
            {titleCol && (
              <Box sx={{ mb: detailCols.length ? 1 : 0, fontWeight: 700, fontSize: '0.9rem' }}>
                {renderColumnValue(titleCol, row)}
              </Box>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {detailCols.map((col) => (
                <Box
                  key={col.field}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1.5,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                      color: 'text.secondary',
                      flexShrink: 0,
                    }}
                  >
                    {col.headerName ?? col.field}
                  </Typography>
                  <Box
                    sx={{
                      fontSize: '0.8125rem',
                      textAlign: 'right',
                      minWidth: 0,
                      overflow: 'hidden',
                    }}
                  >
                    {renderColumnValue(col, row)}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

function DataTable({
  columns,
  rows = [],
  loading = false,
  pagination = true,
  pageSize = 25,
  checkboxSelection = false,
  quickFilter = false,
  stickyHeader = true,
  densitySelector = false,
  emptyState,
  tableId,
  sx,
  ...rest
}) {
  const theme = useTheme();
  // down('sm') === below 768px (phones) in this codebase's breakpoint map.
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [filterText, setFilterText] = useState('');
  const [density, setDensity] = useState('standard');
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize,
  });

  // Persisted column layout (widths + order), loaded once per tableId.
  const [layout, setLayout] = useState(() => loadLayout(tableId));
  React.useEffect(() => {
    setLayout(loadLayout(tableId));
  }, [tableId]);

  // Live column widths applied while dragging the resize handle. Seeded from
  // the persisted layout so a reload restores the user's sizes immediately.
  const [columnWidths, setColumnWidths] = useState(() => loadLayout(tableId)?.widths || {});
  React.useEffect(() => {
    setColumnWidths(loadLayout(tableId)?.widths || {});
  }, [tableId]);

  // Live resize: update width in state on every drag move (not persisted yet).
  const handleColumnResize = useCallback((field, width) => {
    setColumnWidths((prev) => ({ ...prev, [field]: width }));
  }, []);

  // Commit a resize when the drag ends. A null width resets that column to its
  // default. Persists to localStorage when a tableId is provided.
  const handleColumnResizeCommit = useCallback(
    (field, width) => {
      setColumnWidths((prev) => {
        const next = { ...prev };
        if (width == null) delete next[field];
        else next[field] = width;

        if (tableId) {
          setLayout((prevLayout) => {
            const merged = { ...(prevLayout || {}), widths: next };
            saveLayout(tableId, merged);
            return merged;
          });
        }
        return next;
      });
    },
    [tableId]
  );

  // Filter rows based on quick filter text (searches all visible columns)
  const filteredRows = useMemo(() => {
    if (!filterText || filterText.length < 2) return rows;

    const lowerFilter = filterText.toLowerCase();
    return rows.filter((row) =>
      columns.some((col) => {
        const value = row[col.field];
        if (value == null) return false;
        return String(value).toLowerCase().includes(lowerFilter);
      })
    );
  }, [rows, filterText, columns]);

  const handleFilterChange = useCallback((value) => {
    setFilterText(value);
  }, []);

  // Custom empty state overlay
  const slots = useMemo(
    () => ({
      noRowsOverlay: () => (
        <NoRowsOverlay emptyState={emptyState} />
      ),
      noResultsOverlay: () => (
        <NoRowsOverlay
          emptyState={{
            title: 'No results found',
            description: 'Try adjusting your filter criteria.',
            ...emptyState,
          }}
        />
      ),
      ...(rest.slots || {}),
    }),
    [emptyState, rest.slots]
  );

  // Ensure columns have a sensible minimum width and are resizable, then apply
  // any persisted width/order the user previously set for this table.
  const processedColumns = useMemo(() => {
    const withDefaults = columns.map((col) => {
      const resizable = col.resizable !== false;
      return {
        minWidth: 50,
        ...col,
        resizable,
        // Overlay our own resizable header (Community DataGrid can't resize).
        // Preserve any consumer-provided renderHeader by wrapping it.
        renderHeader: (params) => (
          <ResizableColumnHeader
            colDef={{
              ...params.colDef,
              // Keep the original header content if the column supplied one.
              headerName: col.renderHeader
                ? col.renderHeader(params)
                : (col.headerName ?? col.field),
              resizable,
            }}
            onResize={handleColumnResize}
            onResizeCommit={handleColumnResizeCommit}
          />
        ),
      };
    });

    // Apply live widths (seeded from persisted layout, updated during drag).
    const widthMap = { ...(layout?.widths || {}), ...columnWidths };
    const sized = withDefaults.map((col) =>
      widthMap[col.field] != null
        ? { ...col, width: widthMap[col.field], flex: undefined }
        : col
    );

    if (!layout) return sized;

    // Apply saved order (any columns not in the saved order keep their position
    // at the end, preserving newly added columns).
    const order = Array.isArray(layout.order) ? layout.order : null;
    if (!order) return sized;
    const byField = new Map(sized.map((c) => [c.field, c]));
    const ordered = [];
    order.forEach((field) => {
      if (byField.has(field)) {
        ordered.push(byField.get(field));
        byField.delete(field);
      }
    });
    // Append remaining (new) columns in their original order.
    sized.forEach((c) => { if (byField.has(c.field)) ordered.push(c); });
    return ordered;
  }, [columns, layout, columnWidths, handleColumnResize, handleColumnResizeCommit]);

  // Persist a column width change.
  const handleColumnWidthChange = useCallback(
    (params) => {
      if (!tableId) return;
      setLayout((prev) => {
        const next = {
          ...(prev || {}),
          widths: { ...(prev?.widths || {}), [params.colDef.field]: params.width },
        };
        saveLayout(tableId, next);
        return next;
      });
      rest.onColumnWidthChange?.(params);
    },
    [tableId, rest]
  );

  // Persist a column reorder. In MUI X v6 the event provides the moved column's
  // `field` plus its `oldIndex`/`targetIndex`, so we compute the new order from
  // the current field order ourselves.
  const handleColumnOrderChange = useCallback(
    (params) => {
      if (tableId && params?.field != null && typeof params.targetIndex === 'number') {
        setLayout((prev) => {
          // Current known order: saved order, else the incoming column order.
          const currentOrder =
            (prev?.order && prev.order.length ? [...prev.order] : columns.map((c) => c.field));
          const from = currentOrder.indexOf(params.field);
          if (from === -1) return prev;
          currentOrder.splice(from, 1);
          currentOrder.splice(params.targetIndex, 0, params.field);
          const next = { ...(prev || {}), order: currentOrder };
          saveLayout(tableId, next);
          return next;
        });
      }
      rest.onColumnOrderChange?.(params);
    },
    [tableId, columns, rest]
  );

  // Clear the saved layout and return columns to their defaults.
  const handleResetColumns = useCallback(() => {
    if (!tableId) return;
    try { localStorage.removeItem(layoutStorageKey(tableId)); } catch { /* ignore */ }
    setLayout(null);
    setColumnWidths({});
  }, [tableId]);

  const hasCustomLayout = Boolean(
    (columnWidths && Object.keys(columnWidths).length) ||
    (layout && ((layout.widths && Object.keys(layout.widths).length) || (layout.order && layout.order.length)))
  );
  const showToolbar = quickFilter || densitySelector || (tableId && hasCustomLayout);

  return (
    <Box
      sx={{
        width: '100%',
        borderRadius: RADIUS_LG,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        boxShadow: SHADOW[2],
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        ...sx,
      }}
    >
      {/* Quick filter / density / column-reset toolbar */}
      {showToolbar && (
        <Box
          sx={{
            px: { xs: 1.5, sm: 2 },
            py: 1.75,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: { xs: 1, sm: 2 },
            flexWrap: 'wrap',
          }}
        >
          {quickFilter && <QuickFilterInput onFilterChange={handleFilterChange} />}
          {!quickFilter && <Box />}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {tableId && hasCustomLayout && (
              <Tooltip title="Reset column widths & order">
                <Button
                  size="small"
                  variant="text"
                  startIcon={<RestartAltIcon sx={{ fontSize: 16 }} />}
                  onClick={handleResetColumns}
                  sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                >
                  Reset columns
                </Button>
              </Tooltip>
            )}
            {densitySelector && (
              <ToggleButtonGroup
                size="small"
                value={density}
                exclusive
                onChange={(_, val) => val && setDensity(val)}
                aria-label="Table density"
              >
                <ToggleButton value="compact" aria-label="Compact">
                  <Tooltip title="Compact"><DensitySmallIcon sx={{ fontSize: 16 }} /></Tooltip>
                </ToggleButton>
                <ToggleButton value="standard" aria-label="Standard">
                  <Tooltip title="Standard"><DensityMediumIcon sx={{ fontSize: 16 }} /></Tooltip>
                </ToggleButton>
                <ToggleButton value="comfortable" aria-label="Comfortable">
                  <Tooltip title="Comfortable"><DensityLargeIcon sx={{ fontSize: 16 }} /></Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>
            )}
          </Box>
        </Box>
      )}

      {/* DataGrid */}
      <Box
        sx={{
          width: '100%',
          flexGrow: 1,
          // Responsive: horizontal scroll on mobile (<992px)
          [`@media (max-width: ${BP_LAPTOP - 1}px)`]: {
            overflowX: 'auto',
            '& .MuiDataGrid-root': {
              minWidth: 800,
            },
          },
          // Sticky header styling
          ...(stickyHeader && {
            '& .MuiDataGrid-columnHeaders': {
              position: 'sticky',
              top: 0,
              zIndex: 2,
            },
          }),
          // General DataGrid styling
          '& .MuiDataGrid-root': {
            border: 'none',
            fontSize: '0.8125rem',
          },
          // ── Header: theme-driven accent strip + colored bottom border ──
          '& .MuiDataGrid-columnHeaders': {
            position: 'relative',
            bgcolor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.12 : 0.07),
            borderBottom: '2px solid',
            borderColor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.35 : 0.22),
          },
          // Thin gradient accent strip — follows the active theme colors
          '& .MuiDataGrid-columnHeaders::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '3px',
            background: (theme) =>
              `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 50%, ${theme.palette.info.main} 100%)`,
            zIndex: 3,
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 700,
            fontSize: '0.6875rem',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: (theme) =>
              theme.palette.mode === 'dark'
                ? theme.palette.primary.light
                : theme.palette.primary.dark,
          },
          '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
            outline: 'none',
          },
          // Theme-tinted vertical separators between header columns
          '& .MuiDataGrid-columnHeader:not(:last-of-type)': {
            borderRight: '1px solid',
            borderColor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.18 : 0.14),
          },
          // Hide the built-in separator — it is non-interactive in the free
          // Community DataGrid. We render our own drag handle inside each
          // header (see ResizableColumnHeader) instead.
          '& .MuiDataGrid-columnSeparator': {
            display: 'none',
          },
          // Give header content room for our custom resize handle on the right
          // and stop MUI from clipping the absolutely-positioned handle.
          '& .MuiDataGrid-columnHeaderTitleContainer': {
            overflow: 'visible',
          },
          '& .MuiDataGrid-columnHeaderTitleContainerContent': {
            overflow: 'visible',
            width: '100%',
          },
          // The header draggable area sits above the title; make sure our
          // handle (rendered inside the title) can still receive pointer events.
          '& .MuiDataGrid-columnHeaderDraggableContainer': {
            width: '100%',
          },
          // ── Cells: theme-tinted horizontal + vertical grid lines ──
          '& .MuiDataGrid-cell': {
            borderBottom: '1px solid',
            borderColor: 'divider',
            py: 1.25,
            px: 1.5,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          },
          // Theme-tinted vertical separators between body columns
          '& .MuiDataGrid-cell:not(:last-of-type)': {
            borderRight: '1px solid',
            borderColor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.06 : 0.05),
          },
          '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
            outline: 'none',
          },
          '& .MuiDataGrid-columnHeader': {
            px: 1.5,
            overflow: 'visible',
          },
          // ── Rows: theme-tinted zebra striping + smooth hover ──
          '& .MuiDataGrid-row': {
            transition: 'background-color 120ms ease',
          },
          '& .MuiDataGrid-row:nth-of-type(even)': {
            bgcolor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.03 : 0.025),
          },
          '& .MuiDataGrid-row:hover': {
            bgcolor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.12 : 0.07),
          },
          '& .MuiDataGrid-row:last-of-type .MuiDataGrid-cell': {
            borderBottom: 'none',
          },
          // ── SLA-breached row accent (uses theme error color) ──
          '& .sla-breached-row': {
            bgcolor: (theme) => alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.10 : 0.05),
            boxShadow: (theme) => `inset 3px 0 0 0 ${theme.palette.error.main}`,
          },
          '& .sla-breached-row:hover': {
            bgcolor: (theme) => alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.14 : 0.08),
          },
          // ── Footer / pagination polish ──
          '& .MuiDataGrid-footerContainer': {
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.04 : 0.03),
            minHeight: 52,
          },
          '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
            fontSize: '0.8125rem',
            color: 'text.secondary',
          },
          // Remove the default cell selection focus ring for a cleaner look
          '& .MuiDataGrid-cell.MuiDataGrid-cell--editing': {
            boxShadow: 'none',
          },
        }}
      >
        {isMobile ? (
          <MobileCardList
            columns={processedColumns}
            rows={filteredRows}
            onRowClick={rest.onRowClick}
            getRowId={rest.getRowId}
            emptyState={emptyState}
          />
        ) : (
          <DataGrid
            columns={processedColumns}
            rows={filteredRows}
            loading={loading}
            // Pagination
            pagination={pagination}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            // Selection
            checkboxSelection={checkboxSelection}
            disableRowSelectionOnClick={!checkboxSelection}
            keepNonExistentRowsSelected
            // Reorder is disabled so the header drag layer does not swallow the
            // pointer events our resize handle relies on. Resize is our custom
            // right-edge handle (see ResizableColumnHeader).
            disableColumnReorder
            columnBuffer={8}
            // Density & sizing
            density={density}
            autoHeight
            getRowHeight={() => 'auto'}
            // Slots
            slots={slots}
            // Pass all other props first, then our layout handlers so they win
            // (they still invoke any consumer-provided handler internally).
            {...rest}
            onColumnWidthChange={handleColumnWidthChange}
            onColumnOrderChange={handleColumnOrderChange}
          />
        )}
      </Box>
    </Box>
  );
}

export default DataTable;
